import React, { useState } from 'react';
import Select from 'react-select';
import GetAppIcon from '@mui/icons-material/GetApp';
import { saveAs } from 'file-saver'; // To save the CSV file

import useFetchData from '../hooks/useFetchData';
import useFetchAnalytics from '../hooks/useFetchAnalytics';
import TableWrapper from '../utils/TableWrapper';
import Spinner from './spinner/Spinner';

export default function SponsoredCohortGroupMembers() {
  const [cohortId, setCohortId] = useState('');
  const [cohortName, setCohortName] = useState('');

  const columns = [
    {
      title: 'Group Name',
      field: 'group_name',
      render: (row) => <p className="capitalize">{row?.group_name}</p>,
    },
    {
      title: 'First Name',
      field: 'firstname',
      render: (row) => <p className="capitalize">{row?.firstname}</p>,
    },
    {
      title: 'Last Name',
      field: 'lastname',
      render: (row) => <p className="capitalize">{row?.lastname}</p>,
    },
    {
      title: 'Email',
      field: 'email',
      render: (row) => <p className="capitalize">{row?.email}</p>,
    },
    { title: 'Phone Number', field: 'phone_number' },
    {
      title: 'Last Login',
      field: 'last_login',
      render: (row) => (
        <p>
          {row?.last_login
            ? new Date(row?.last_login).toLocaleString(undefined, {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
              })
            : 'No Record'}
        </p>
      ),
    },
  ];

  // get cohorts
  const { data: cohorts } = useFetchData(
    ['cohorts'],
    '/cohorts',
    {},
    "Couldn't get cohorts. Please try again!",
    true
  );

  const cohortOptions = cohorts
    ? cohorts?.data?.cohorts?.map((cohort) => ({
        value: cohort.id,
        label: cohort.name,
      }))
    : [];

  const { data: cohortGroupMembersRawData, isLoading } = useFetchAnalytics(
    ['cohort-group-members', cohortId],
    `/cohort-group-members/${cohortId}/raw-data`,
    { params: {} },
    "Couldn't get cohort group members raw data. Please try again",
    cohortId
  );
  // Function to export the data to CSV
  const exportDataToCSV = () => {
    const datasets = cohortGroupMembersRawData?.data
      ? cohortGroupMembersRawData?.data
      : [];

    const flattenedData = datasets.map((dataset) => {
      return {
        group_name: dataset.group_name,
        firstname: dataset.student.firstname,
        lastname: dataset.student.lastname,
        email: dataset.student.email,
        phone_number: dataset.student.phone_number,
        last_login: dataset.student.last_login
          ? new Date(dataset.student.last_login).toLocaleString(undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              timeZoneName: 'short',
            })
          : 'No Record',
      };
    });
    const labels = Object.keys(flattenedData[0]);
    if (!datasets.length && !labels.length) {
      return;
    }
    // CSV header
    let csvContent = `${labels.join(',')}\n`;

    // Assuming `datasets` is an array of values for each label
    flattenedData.forEach((dataset) => {
      const values = Object.values(dataset);
      csvContent += `${values.join(',')}\n`;
    });

    // Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${cohortName}.csv`);
  };

  return (
    <>
      <div className="flex justify-between m-2">
        <h2>Cohort: {cohortName}</h2>
        <button
          type="button"
          onClick={exportDataToCSV}
          className="flex items-center space-x-2 bg-claret-500 px-4 py-1 rounded-md text-white text-sm"
        >
          <GetAppIcon className="inherit" />
          <span>Export Data as CSV</span>
        </button>
      </div>
      <div className="gap-4 grid grid-cols-4 bg-white mt-4 p-3 rounded-lg">
        <Select
          isClearable
          placeholder="Select Cohort"
          className="react-dropdown"
          classNamePrefix="dropdown"
          options={cohortOptions}
          onChange={(event) => {
            setCohortId(event?.value || '');
            setCohortName(event?.label || '');
          }}
        />
      </div>{' '}
      <div className="mt-4">
        {isLoading && cohortId ? (
          <div className="flex justify-center items-center bg-white">
            <Spinner />
          </div>
        ) : (
          <TableWrapper
            title={`${cohortName} Students`}
            columns={columns}
            data={
              cohortGroupMembersRawData?.data
                ? cohortGroupMembersRawData?.data.map((dataset) => ({
                    group_name: dataset.group_name,
                    firstname: dataset.student.firstname,
                    lastname: dataset.student.lastname,
                    email: dataset.student.email,
                    phone_number: dataset.student.phone_number,
                    last_login: dataset.student.last_login
                      ? new Date(dataset.student.last_login).toLocaleString(
                          undefined,
                          {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            timeZoneName: 'short',
                          }
                        )
                      : 'No Record',
                  }))
                : []
            }
            options={{
              rowStyle: {
                overflowWrap: 'break-word',
              },
            }}
          />
        )}
      </div>
    </>
  );
}
